import * as React from "react"
import classNames from "classnames"
import { RxEnvelopeClosed } from "react-icons/rx"

type EmailProps = {
  value: string
  onChange: (value: string) => void
  label: string
  error?: string
  id?: string
  required?: boolean
  showLabel?: boolean
  placeholder?: string
  variant?: "outline"
  compact?: boolean
  classes?: string
  prefixWithIcon?: boolean
}

const getInputVariantClasses = (variant?: "outline", compact?: boolean) => {
  if (variant === "outline") {
    return "border rounded-md h-9"
  }

  if (compact) {
    // md:h-auto matches the default branch: Tailwind ignores h-[56] (no unit).
    return "bg-gray50 h-10 md:h-auto"
  }

  return "bg-gray50 h-[56]"
}

export const Email = React.forwardRef<HTMLInputElement, EmailProps>(
  function EmailInput(props: EmailProps, ref) {
    const {
      showLabel = true,
      placeholder,
      classes,
      prefixWithIcon,
      compact = false,
    } = props
    const hiddenLabelMargin = compact ? "md:mt-4" : "mt-4"

    return (
      <div>
        <div
          className={classNames("relative", !showLabel && hiddenLabelMargin)}
        >
          {showLabel && (
            <label
              className={classNames(
                "text-gray16 uppercase flex pb-4 text-sm lg:text-base",
                props.error != null && "text-red-500",
                classes,
              )}
              htmlFor={props.id}
            >
              {prefixWithIcon && (
                <span className="pr-2">
                  <RxEnvelopeClosed size={24} className="text-gold" />
                </span>
              )}
              {props.label}{" "}
              {props.required && <span className="text-gray16">*</span>}
            </label>
          )}
          <input
            type="email"
            ref={ref}
            className={classNames(
              "w-full relative text-sm lg:text-base focus:outline-none",
              compact ? "px-3 py-2 md:p-4" : "p-4",
              props.error != null
                ? "border-red-500 text-red-500"
                : "text-neutral-600 border-black border-b",
              getInputVariantClasses(props.variant, compact),
            )}
            value={props.value}
            onChange={(event) => props.onChange(event.target.value)}
            aria-label="Email"
            id={props.id}
            placeholder={!showLabel ? placeholder : ""}
          />
        </div>
        {props.error != null && (
          <span className="text-red-500 text-sm block">{props.error}</span>
        )}
      </div>
    )
  },
)
